import { useEffect, useRef, useState, type ReactNode } from "react";

import {
  boardPage,
  EXPORTS,
  ORDER_CHOICES,
  PER_PAGE_CHOICES,
  type BoardOrder,
  type BoardRow,
  type ExportFormat,
} from "../board.ts";
import { stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { RowDetail } from "./RowDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * The board every page of `17 WP16 Tenant Admin.dc.html` is, drawn once.
 *
 * That file renders all fifteen of its pages from one `PAGES` row and one `gridUI()`: a crumb
 * trail, a title with a mono source chip and a primary command, a sentence, four KPI tiles, a card
 * holding a toolbar, a header strip, rows, an empty state and a pager, then a closing callout — and
 * a detail panel over the top of it when a row is clicked. Every value below is that file's, read
 * from its own inline styles and already measured into `styles.css` by the four screens built
 * before these five.
 *
 * **Why this component exists and those four do not use it.** Five more pages of the same template
 * were about to be five more ~500-line copies of search, sort, paging, three export formats, an
 * empty state and a pager. The four that exist are each pinned by a committed fidelity check, and
 * rewriting a measured screen to share code with an unmeasured one is how a green check stops
 * meaning anything — so they are left exactly as they are, and this is written for the five that
 * were not drawn yet. `docs/OPEN_WORK.md` carries the consolidation with that condition on it.
 *
 * What a page keeps for itself is everything that is a statement about a service: which operation
 * it reads, what it makes of a row, what state a row is in, which filter its listing can honestly
 * offer, and the words. This component decides nothing about any of that. It owns the three things
 * the design makes identical on every page — the grid's behaviour, the states, and the geometry.
 *
 * Design rule 4 is satisfied here rather than nine times: search, filter, sort, pagination with
 * rows-per-page, export to CSV, Excel and PDF, and an empty state with a Clear filters button.
 */

export interface RecordsBoardProps {
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs: readonly string[];
  readonly title: string;
  /** The service codes behind the page, in the mono chip the design puts beside the title. */
  readonly source: string;
  readonly description: string;
  readonly columns: readonly string[];
  /** `undefined` until the listing has answered, which is the design's loading state. */
  readonly rows: readonly BoardRow[] | undefined;
  /** Whatever went wrong, already mapped to a state by `view-state.ts`. */
  readonly failure?: ViewState;
  readonly onRetry: () => void;
  /** The empty state's sentence: what would have to happen for a row to appear. */
  readonly purpose: string;
  /**
   * The design's primary command, and why it is refused.
   *
   * Every one of these five pages has a designed form whose required fields include at least one
   * with no field on the wire, so all five are drawn and disabled with the measured reason on the
   * button — the same call the four existing screens made. A page offering a command it cannot send
   * is the stub `PageOutlook` exists to avoid.
   */
  /**
   * Absent on a board that is a report rather than a register.
   *
   * The five designed pages all have one. The four analytics boards have none, and giving them a
   * token command so this prop could stay required would be drawing a button nothing asked for —
   * which is the same fault as drawing one the service cannot serve, from the other direction.
   * Export already sits in the toolbar where rule 4 puts it.
   *
   * **A primary may now act.** Every one before the driver-onboarding board refused, because no
   * command on those pages had a client that could spend its scope — so `refusal` was the whole
   * vocabulary and a disabled button carrying its reason was the honest drawing. The onboarding
   * board approves a DRV-03 document, which is a command this console can actually issue, so the
   * prop takes either shape: a `refusal` draws the disabled button exactly as before, and an
   * `onPress` draws an enabled one. Nothing that existed changes, which is why the union is by
   * presence rather than a `kind` field every caller would have had to add.
   */
  readonly primary?:
    | { readonly label: string; readonly refusal: string }
    | {
        readonly label: string;
        readonly onPress: () => void;
        /** Pressed and not yet answered. The button says so and refuses a second press. */
        readonly busy?: boolean;
        /** Why it cannot be pressed right now, where that is a property of the rows. */
        readonly unavailable?: string;
      };
  /** The filter's options, in the service's own vocabulary, and the design's word for unfiltered. */
  readonly filter: {
    readonly unfiltered: string;
    readonly options: readonly { readonly value: string; readonly label: string }[];
    readonly value: string;
    readonly onChange: (value: string) => void;
  };
  /**
   * A second command beside the primary, where a board has two.
   *
   * The merchants board is the first: a shop is **admitted** and then **let trade**, and MER-01
   * keeps those apart so an audit log can show who took the application and who checked it.
   * Collapsing them into one button would hide a separation the service went to the trouble of
   * modelling. No designed page has two commands, so this is drawn in the console's own
   * secondary style rather than copied from one.
   */
  readonly secondary?: {
    readonly label: string;
    readonly onPress: () => void;
    readonly busy?: boolean;
  };
  /**
   * The design's add form, which the primary button opens.
   *
   * Every page on this console draws one and every one of them is disabled, because "every
   * one of these five pages has a designed form whose required fields include at least one
   * with no field on the wire". The merchants board is the first whose form can be submitted:
   * MER-01's create takes four fields and an operator supplies all four, so there is nothing
   * to invent and nothing to leave blank.
   *
   * Open state is the caller's, not this component's, because the caller is what knows
   * whether a submission succeeded and should close it.
   */
  readonly form?: {
    readonly open: boolean;
    readonly title: string;
    /**
     * The glyph for the design's 40px tile, as a Material Symbols Rounded name.
     *
     * The page's own rail glyph, not a new one. `17 WP16 Tenant Admin`'s `FORMS` table gives every
     * designed form an icon — `settings`, `toggle_on`, `palette`, `person_add` — but neither screen
     * that can actually open this form is in that table, so taking the icon the page already shows
     * in the rail is the choice that invents nothing. Q-288.
     */
    readonly icon?: string;
    /** The design's subtitle under the title: a sentence, where the row detail's is an id. */
    readonly subtitle?: string;
    /** The design's uppercase `fieldset` legend. One section; the designed forms have one or two. */
    readonly legend: string;
    /**
     * The form's fields, in the order the service's own schema lists them.
     *
     * Five kinds, and each exists because a contract asked for it rather than because a form
     * library offers it: MER-01's create is four strings, and MER-02's branch is a code, a name,
     * an address, two coordinates, a time zone, a preparation time and a set of fulfilment modes.
     * A board that drew all of those as text boxes would be asking an operator to type a number
     * into a string and hoping.
     */
    readonly fields: readonly {
      readonly name: string;
      readonly label: string;
      readonly type?: "text" | "email" | "number" | "select" | "modes";
      readonly value: string;
      readonly onChange: (value: string) => void;
      /** What this field is for, where the service's own constraint is worth saying. */
      readonly hint?: string;
      /** For `select`: the values the contract publishes, and nothing else. */
      readonly options?: readonly { readonly value: string; readonly label: string }[];
      /**
       * For `modes`: a set rather than a choice, carried as a comma-separated value.
       *
       * A string rather than an array so one `onChange` serves every field kind. The caller
       * splits it, which is where the contract's own bound on how many may be chosen lives.
       */
      readonly choices?: readonly { readonly value: string; readonly label: string }[];
      /** `min` and `max` for a number, from the schema rather than from judgement. */
      readonly min?: number;
      readonly max?: number;
      readonly step?: string;
    }[];
    readonly submitLabel: string;
    readonly onSubmit: () => void;
    readonly onCancel: () => void;
    readonly busy?: boolean;
    /** A refusal the service gave, in its own words. */
    readonly failure?: string;
  };
  /** The design's closing callout, in one of its three kinds. */
  readonly note: {
    readonly kind: "info" | "warn" | "lock";
    readonly glyph: string;
    readonly text: string;
  };
  /** What the board fetched, where that is worth saying. `boundedNote` decides. */
  readonly bounded?: string;
  /**
   * A sentence under the grid saying what the listing is a list of, where that is not obvious.
   *
   * The service catalogue's precedent: a board whose operation silently excludes records is a
   * complete-looking list with records missing from it, and the fix is a sentence rather than a
   * guess at the total.
   */
  readonly footnote?: string;
  /** The basename an export writes, and the heading of its first column set. */
  readonly exportName: string;
  /** The design's detail-panel subtitle: the page's title and its source chip. */
  readonly detailSubtitle: string;
  /** The sentence the design repeats inside the detail panel, which is the page's closing note. */
  readonly detailNote: string;
}

/** Derived rather than declared twice, so a field added to the props cannot be missed here. */
type RecordsFormField = NonNullable<RecordsBoardProps["form"]>["fields"][number];

/**
 * The form's fields, drawn into the design's two-column grid.
 *
 * A function rather than JSX inline in the dialog, because the dialog is now three rows of chrome
 * around them and nesting the whole switch inside the middle row made the shape of the dialog hard
 * to see. The field logic below is unchanged.
 *
 * `--wide` on the checkbox set, which is the rule `formUI()` follows: it puts an area, a chip row
 * and a file drop across both columns, and a row of checkboxes is this build's chip row.
 */
function renderFields(fields: readonly RecordsFormField[]): ReactNode {
  return fields.map((field) => (
    <label
      key={field.name}
      className={field.type === "modes" ? "records__field records__field--wide" : "records__field"}
    >
      <span>{field.label}</span>
      {field.type === "select" ? (
        <select
          value={field.value}
          required
          onChange={(event) => {
            field.onChange(event.target.value);
          }}
        >
          <option value="">Choose…</option>
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : field.type === "modes" ? (
        /*
         * A set, drawn as checkboxes rather than a multi-select listbox: the contract
         * publishes three fulfilment modes and at least one must be chosen, and three
         * checkboxes say that where a listbox hides it behind a scroll and a modifier key.
         */
        <span className="records__choices">
          {(field.choices ?? []).map((choice) => {
            const chosen = field.value.split(",").filter(Boolean);
            return (
              <label key={choice.value} className="records__choice">
                <input
                  type="checkbox"
                  checked={chosen.includes(choice.value)}
                  onChange={(event) => {
                    const next = event.target.checked
                      ? [...chosen, choice.value]
                      : chosen.filter((value) => value !== choice.value);
                    field.onChange(next.join(","));
                  }}
                />
                {choice.label}
              </label>
            );
          })}
        </span>
      ) : (
        <input
          type={field.type ?? "text"}
          value={field.value}
          required
          {...(field.min === undefined ? {} : { min: field.min })}
          {...(field.max === undefined ? {} : { max: field.max })}
          {...(field.step === undefined ? {} : { step: field.step })}
          onChange={(event) => {
            field.onChange(event.target.value);
          }}
        />
      )}
      {field.hint === undefined ? null : <small className="records__hint">{field.hint}</small>}
    </label>
  ));
}

export function RecordsBoard(props: RecordsBoardProps): ReactNode {
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<BoardOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const [openRow, setOpenRow] = useState<string | undefined>(undefined);
  const exportRegion = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const view = boardPage(props.rows ?? [], { search, order, page, perPage });

  /** Whether anything the administrator chose is narrowing the board. */
  const narrowed = props.filter.value !== "" || search.trim().length > 0;

  /*
   * Which of the six states this board is in.
   *
   * **`stateForList` is consulted for the unfiltered cases and deliberately not for the filtered
   * one, and that is a defect in the four screens built before these five rather than a difference
   * of taste.** Handed an empty list with `filtered: true` it answers `empty/filtered`, and
   * `StateView` then draws "Nothing matches this filter · Clear the filter to see everything on
   * this page" **in place of its children** — so the grid's own `search_off` block, with the
   * **Clear filters button design rule 4 requires**, is never rendered. On those four screens the
   * button is reachable only when a client-side search empties a board that had rows; choose a
   * server-side filter that answers nothing and the sentence tells the administrator to clear a
   * filter and gives them no way to.
   *
   * The design has no such split: its `grid.empty` block sits inside the card, above the pager,
   * whenever the row count is zero for any reason. So a narrowed board stays `ready` here and the
   * card draws its own empty state, and `StateView`'s empty state is kept for the one case it is
   * right for — a tenant that genuinely has no rows, where there is no filter to clear and the
   * sentence worth showing is what would make one appear. Recorded in `docs/OPEN_WORK.md` against
   * the four earlier screens, because fixing them means re-measuring them.
   *
   * No `observedAt` on any of these four listings, so no stale state is reachable. Each publishes
   * per-row instants — `updatedAt`, `approvedAt` — and no page-level instant saying when the answer
   * was composed. A configuration board is not a live board, so this is the least costly place that
   * absence has been found; it is recorded all the same, because the recommendation is the same one
   * and it is one field. R-08.
   */
  const viewState: ViewState =
    props.failure ??
    (narrowed && props.rows !== undefined
      ? { kind: "ready", stale: false }
      : stateForList(props.rows));

  const clearFilters = (): void => {
    setSearch("");
    props.filter.onChange("");
    setPage(1);
  };

  const detail = view.all.find((row) => row.id === openRow);

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      {props.columns.map((column) => (
        <span key={column}>{column}</span>
      ))}
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <section className="page records">
      <PageHeading
        crumbs={props.crumbs}
        title={props.title}
        source={props.source}
        description={props.description}
        action={
          <>
            {props.primary === undefined ? null : "refusal" in props.primary ? (
              <button
                type="button"
                className="records__primary"
                disabled
                title={props.primary.refusal}
              >
                <span aria-hidden="true" className="records__primary-glyph">
                  add
                </span>
                {props.primary.label}
              </button>
            ) : (
              /*
               * The acting shape. Disabled while busy or while unavailable, and in both cases the
               * reason is on the title rather than implied by the button being grey — a control that
               * cannot be pressed and does not say why is the same fault as a column drawn empty.
               */
              <button
                type="button"
                className="records__primary"
                disabled={props.primary.busy === true || props.primary.unavailable !== undefined}
                {...(props.primary.unavailable === undefined
                  ? {}
                  : { title: props.primary.unavailable })}
                onClick={props.primary.onPress}
              >
                <span aria-hidden="true" className="records__primary-glyph">
                  add
                </span>
                {props.primary.busy === true ? "Working…" : props.primary.label}
              </button>
            )}
            {props.secondary === undefined ? null : (
              <button
                type="button"
                className="records__secondary"
                disabled={props.secondary.busy === true}
                onClick={props.secondary.onPress}
              >
                {props.secondary.busy === true ? "Working…" : props.secondary.label}
              </button>
            )}
          </>
        }
      />

      {/*
        No tiles, where the design has four on every one of these pages.

        Twenty tiles across the five, and not one has an operation behind it. Nine are counts or
        values derived from a count — R-04's measurement is that none of these four listings
        publishes a total, so a tile reading `24` would mean "24 so far" under a label that means
        "24". Nine more are a page's own absent column restated: all four of `config`'s are Q-104,
        all four of `plan`'s are Q-105, and `policies`'s `Next effective change` is Q-101. The last
        two, `Surge zones now` and `Promotions`, belong to the dynamic-pricing and promotion
        services, neither of which is an owner of the pricing page. Each page's component names its
        own four.
      */}

      {props.form !== undefined && props.form.open ? (
        /*
         * The design's form is a dialog over the board, not a panel above it.
         *
         * `17 WP16 Tenant Admin`'s `formUI()` draws it with the same `fm.overlay` and `fm.panel`
         * the row detail uses — which is why `styles.css` shares those two rules between them
         * rather than keeping two copies equal by hand. Q-288.
         *
         * The overlay closes on a click and on Escape; a click inside the panel is not one. The
         * same pair `SurgeZones` and `Geofences` already use in the dispatch console, which built
         * this dialog from the same source first.
         */
        <div
          className="records__form-overlay"
          role="presentation"
          onClick={props.form.onCancel}
          onKeyDown={(event) => {
            if (event.key === "Escape") props.form?.onCancel();
          }}
        >
          <form
            className="records__form"
            role="dialog"
            aria-modal="true"
            aria-label={props.form.title}
            onClick={(event) => {
              event.stopPropagation();
            }}
            onSubmit={(event) => {
              event.preventDefault();
              props.form?.onSubmit();
            }}
          >
            <div className="records__form-head">
              {props.form.icon === undefined ? null : (
                <span aria-hidden="true" className="records__form-glyph">
                  {props.form.icon}
                </span>
              )}
              <span className="records__form-heading">
                <strong className="records__form-title">{props.form.title}</strong>
                {props.form.subtitle === undefined ? null : (
                  <span className="records__form-sub">{props.form.subtitle}</span>
                )}
              </span>
              <button
                type="button"
                className="records__form-close"
                aria-label="Close"
                onClick={props.form.onCancel}
                disabled={props.form.busy === true}
              >
                close
              </button>
            </div>

            <div className="records__form-body">
              {props.form.failure === undefined ? null : (
                /*
                 * The service's own words, not a rewritten summary. An operator who is told
                 * "something went wrong" has to ask somebody; one who is told what the service
                 * said can often fix it themselves.
                 *
                 * First in the body, where the design puts it, so it cannot be scrolled past.
                 */
                <p className="records__form-failure" role="alert">
                  <span aria-hidden="true" className="records__form-failure-glyph">
                    error
                  </span>
                  <span>{props.form.failure}</span>
                </p>
              )}

              <fieldset className="records__fieldset">
                <legend>{props.form.legend}</legend>
                <div className="records__field-grid">{renderFields(props.form.fields)}</div>
              </fieldset>
            </div>

            <div className="records__form-foot">
              {/* Cancel first, which is the order the design's footer draws them in. */}
              <button
                type="button"
                className="records__form-cancel"
                onClick={props.form.onCancel}
                disabled={props.form.busy === true}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="records__form-save"
                disabled={props.form.busy === true}
              >
                {props.form.busy === true ? "Working…" : props.form.submitLabel}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="records__panel">
        <div className="records__toolbar">
          <span className="records__search">
            <span aria-hidden="true" className="records__search-glyph">
              search
            </span>
            <input
              type="search"
              aria-label="Search"
              placeholder="Search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </span>
          <select
            aria-label="Filter by state"
            value={props.filter.value}
            onChange={(event) => {
              props.filter.onChange(event.target.value);
              setPage(1);
            }}
          >
            <option value="">{props.filter.unfiltered}</option>
            {props.filter.options.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Sort"
            value={order}
            onChange={(event) => {
              setOrder(event.target.value as BoardOrder);
            }}
          >
            {ORDER_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <span className="records__export" ref={exportRegion}>
            <button
              type="button"
              className="records__export-button"
              aria-expanded={exportOpen}
              onClick={() => {
                setExportOpen((open) => !open);
              }}
            >
              <span aria-hidden="true" className="records__export-glyph">
                download
              </span>
              Export
            </button>
            {exportOpen ? (
              <div className="records__export-menu" role="menu">
                {EXPORTS.map((choice) => (
                  <button
                    key={choice.format}
                    type="button"
                    role="menuitem"
                    className="records__export-item"
                    onClick={() => {
                      setExportOpen(false);
                      exportRows(view.all, props.columns, choice.format, props.exportName);
                    }}
                  >
                    <span aria-hidden="true" className="records__export-item-glyph">
                      {choice.glyph}
                    </span>
                    {choice.label}
                  </button>
                ))}
              </div>
            ) : null}
          </span>
        </div>

        <StateView
          state={viewState}
          purpose={props.purpose}
          onRetry={() => {
            props.onRetry();
          }}
          skeleton={{
            head,
            // The columns this board was given, plus the `State` column it adds itself.
            columns: props.columns.length + 1,
            rowClassName: "records__row",
            page: props.title,
            // The first crumb is the tenant, which is how this board is always called.
            tenant: props.crumbs[0] ?? "this tenant",
          }}
        >
          {head}
          <div className="records__rows">
            {view.rows.map((row) => (
              <div
                key={row.id}
                className={
                  row.tinted === true ? "records__row records__row--cross" : "records__row"
                }
                /*
                 * `div role="button" tabindex="0"`, which is the design's own markup for a grid row
                 * — not a `<button>`. A real button brings the user agent's border, background,
                 * font and text alignment, and resetting those is five rules of guesswork where the
                 * design has already decided: its row is a grid container that happens to be
                 * operable.
                 *
                 * The four screens built before these five draw a plain `div` with no role, no tab
                 * stop and no click, which is the one place their markup is not this design's
                 * behaviour: that file puts `cursor: pointer`, `role`, `tabindex` and an `onClick`
                 * on every row of every page, and opens a detail panel from it.
                 *
                 * The keyboard handler is this console's rather than the design's. That file binds
                 * only `onClick`, so its rows are reachable by Tab and cannot be activated — which
                 * is a defect in the reference rather than a decision to copy.
                 */
                role="button"
                tabIndex={0}
                onClick={() => setOpenRow(row.id)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  setOpenRow(row.id);
                }}
              >
                {row.cells.map((cell, index) => (
                  <span
                    key={props.columns[index] ?? String(index)}
                    className={cellClass(index, cell.mono === true, cell.absent === true)}
                    {...(cell.title === undefined ? {} : { title: cell.title })}
                  >
                    {cell.absent === true ? "—" : cell.text}
                  </span>
                ))}
                <span className="records__state">
                  <StateBadge
                    code={props.source}
                    status={row.state.label}
                    classKey={row.state.classKey}
                    label={row.state.label}
                    {...(row.state.title === undefined ? {} : { title: row.state.title })}
                  />
                </span>
              </div>
            ))}
          </div>
          {view.total === 0 ? (
            <div className="records__no-match">
              <span aria-hidden="true" className="records__no-match-glyph">
                search_off
              </span>
              Nothing matches this search or filter.
              <button type="button" className="records__clear" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          ) : null}
          <div className="records__pager">
            <span>
              {view.total === 0
                ? "No matching rows"
                : `Showing ${String((Math.min(page, view.pageCount) - 1) * perPage + 1)}–${String(Math.min(Math.min(page, view.pageCount) * perPage, view.total))} of ${String(view.total)}`}
            </span>
            <span className="records__pager-controls">
              <select
                aria-label="Rows per page"
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
              >
                {PER_PAGE_CHOICES.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice} per page
                  </option>
                ))}
              </select>
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <span aria-hidden="true">chevron_left</span>
              </button>
              <span className="records__page">
                Page {Math.min(page, view.pageCount)} of {view.pageCount}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={page >= view.pageCount}
                onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
              >
                <span aria-hidden="true">chevron_right</span>
              </button>
            </span>
          </div>
        </StateView>

        {props.bounded === undefined ? null : (
          <p className="records__bounded" role="status">
            {props.bounded}
          </p>
        )}
        {props.footnote === undefined ? null : <p className="records__bounded">{props.footnote}</p>}
      </div>

      <div className={`records__note records__note--${props.note.kind}`}>
        <span aria-hidden="true" className="records__note-glyph">
          {props.note.glyph}
        </span>
        <span>{props.note.text}</span>
      </div>

      {detail === undefined ? null : (
        <RowDetail
          row={detail}
          columns={props.columns}
          subtitle={props.detailSubtitle}
          source={props.source}
          note={props.detailNote}
          onClose={() => setOpenRow(undefined)}
        />
      )}
    </section>
  );
}

/**
 * Which of the grid's three cell rules a column takes.
 *
 * The design's own rule: the first cell is the row's name at 600 in `--text`, the rest at 400 in
 * `--text2`. The class names are the ones already measured into `styles.css` by the audit board,
 * whose first column is a clock — hence `__when` for a cell that is usually a name.
 */
function cellClass(index: number, mono: boolean, absent: boolean): string {
  const base = index === 0 ? "records__when" : index === 1 ? "records__actor" : "records__action";
  return `${base}${mono ? " records__cell--mono" : ""}${absent ? " records__cell--absent" : ""}`;
}

/**
 * Export what the administrator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen rather than the raw
 * values — so an exported row says the same thing the board did, including an absent cell, which is
 * written as the design's own `—` rather than as an empty field. An empty field in a spreadsheet
 * reads as "nothing here"; `—` under a column that can never be filled is the same statement the
 * screen made.
 */
function exportRows(
  rows: readonly BoardRow[],
  columns: readonly string[],
  format: ExportFormat,
  name: string,
): void {
  const header = [...columns, "State"];
  const body = rows.map((row) => [
    ...row.cells.map((cell) => (cell.absent === true ? "—" : cell.text)),
    row.state.label,
  ]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), `${name}.csv`);
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      `${name}.xls`,
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>${escapeHtml(name)}</title></head><body><h2 style="font-family:sans-serif">${escapeHtml(name)}</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}
